<script setup lang="ts">
/**
 * PrivacyView — политика конфиденциальности (inbox-337).
 *
 * 152-ФЗ baseline: ст. 18 (информирование субъекта ПДн), цели обработки,
 * категории данных, права субъекта, контакты оператора. Не legal-bullet-proof —
 * перед production review @user/legal должен сверить с актуальной редакцией ФЗ.
 */
import AppPublic from '@/layouts/AppPublic.vue';
import { usePageHead } from '@/composables/usePageHead';

usePageHead({
  title: 'Политика конфиденциальности',
  description: 'Как link.helmix.app обрабатывает персональные данные. 152-ФЗ.',
  path: '/privacy',
  type: 'website',
});
</script>

<template>
  <AppPublic>
    <article class="legal">
      <div class="container">
        <header class="head">
          <span class="eyebrow">Политика конфиденциальности</span>
          <h1>Обработка персональных данных в link.helmix.app</h1>
          <p class="meta">Действует с 21 мая 2026. Версия 1.0.</p>
        </header>

        <section>
          <h2>1. Оператор</h2>
          <p>
            Сервис link.helmix.app (далее — «Сервис», «мы»). Оператор персональных данных —
            <strong>ООО «Хелмикс Лабс»</strong>, ОГРН 1234567890123, ИНН 7700123456.
            Адрес: 123022, г. Москва, ул. Примерная, д. 1, оф. 101. Уведомление в
            Роскомнадзоре от 2026-05-19 № 77-26-XXXXXX.
          </p>
        </section>

        <section>
          <h2>2. Категории обрабатываемых данных</h2>
          <ul>
            <li>Идентификационные: email, имя, организация, телефон (если указан).</li>
            <li>Технические: IP-адрес, User-Agent, страна (через MaxMind GeoLite2),
                отпечаток устройства (visitor_id, хешированный).</li>
            <li>Платёжные: только маска карты («•••• 4242») и метаданные транзакций;
                полные реквизиты остаются у платёжного провайдера.</li>
            <li>Метаданные использования: создаваемые ссылки, статистика кликов,
                настройки кампаний.</li>
          </ul>
        </section>

        <section>
          <h2>3. Цели обработки</h2>
          <ul>
            <li>Предоставление доступа к функциям Сервиса (создание ссылок, аналитика,
                bio-страницы, QR-коды, AI-копайлот).</li>
            <li>Биллинг и выставление документов закрытия (счёт, акт, счёт-фактура).</li>
            <li>Антифрод: распознавание ботов и злоупотреблений.</li>
            <li>Обязательное уведомление о существенных изменениях Сервиса.</li>
          </ul>
        </section>

        <section>
          <h2>4. Сроки и хранение</h2>
          <p>
            Все данные хранятся на серверах Yandex Cloud в РФ (зоны ru-central1-a/b/c).
            Шифрование at-rest. Срок хранения: пока активен аккаунт + 6 месяцев после
            удаления (153-ФЗ). Логи кликов с PII (IP) — 90 дней, далее анонимизируются.
          </p>
        </section>

        <section>
          <h2>5. Передача третьим лицам</h2>
          <p>
            Мы не продаём и не передаём данные. Технические партнёры: Yandex Cloud
            (хостинг), CloudPayments / ЮKassa (платежи), MaxMind (геолокация по IP).
            Каждый партнёр имеет соответствующее соглашение и обработка ограничена
            техническими задачами.
          </p>
        </section>

        <section>
          <h2>6. Права субъекта (ст. 14, 152-ФЗ)</h2>
          <ul>
            <li>Доступ к своим данным — экспорт через личный кабинет.</li>
            <li>Уточнение, блокирование или удаление — заявка через /contact.</li>
            <li>Отзыв согласия — приводит к деактивации аккаунта.</li>
            <li>Жалоба в Роскомнадзор: <code>rkn.gov.ru</code>.</li>
          </ul>
        </section>

        <section>
          <h2>7. Контакты по вопросам ПДн</h2>
          <p>
            Запросы по ПДн: <a href="mailto:privacy@helmix.app">privacy@helmix.app</a>.
            Срок ответа — до 30 дней (ст. 20, 152-ФЗ).
          </p>
        </section>

        <p class="foot-note">
          ⚠ Это базовая редакция. Перед запуском платных тарифов перепроверьте у юриста
          актуальные требования (152-ФЗ, 149-ФЗ, GDPR если будут EU-клиенты).
        </p>
      </div>
    </article>
  </AppPublic>
</template>

<style scoped>
.legal { padding: 48px 0 96px; color: #c5cdd6; line-height: 1.65; }
.container { max-width: 760px; margin: 0 auto; padding: 0 24px; }
.head { text-align: center; margin-bottom: 40px; }
.eyebrow { display: inline-block; padding: 4px 12px; background: rgba(79,140,255,0.10); color: #6f9eff; border-radius: 100px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 14px; }
.head h1 { font-size: 32px; line-height: 1.2; letter-spacing: -0.02em; font-weight: 700; color: #e8edf3; margin-bottom: 8px; }
.meta { font-size: 13px; color: #8b95a5; }
section { margin-bottom: 28px; }
section h2 { font-size: 18px; font-weight: 600; color: #e8edf3; margin-bottom: 10px; }
section p, section ul { font-size: 14.5px; }
section ul { padding-left: 22px; }
section li { margin-bottom: 6px; }
section a { color: #6f9eff; }
section code { font-family: 'SF Mono', Menlo, monospace; font-size: 13px; color: #6f9eff; background: rgba(0,0,0,0.25); padding: 1px 6px; border-radius: 4px; }
.foot-note { margin-top: 36px; padding: 14px 18px; background: rgba(245,165,36,0.08); border-left: 3px solid #f5a523; border-radius: 4px; font-size: 13px; color: #c5cdd6; }
</style>
